import React from "react";
import { makeStyles, Theme } from "@material-ui/core";
import { green, orange } from "@material-ui/core/colors";
import ToggleButton from "@material-ui/lab/ToggleButton";
import ToggleButtonGroup from "@material-ui/lab/ToggleButtonGroup";
import { OFFLINE_AFTER_MS, OnlineStatus } from "./useLiveDatas";

// the height of the app's navbar, which is sticky at the top of the page; the
// filter sticks just below it
const NAVBAR_HEIGHT = 66;

const OFFLINE_AFTER_MIN = OFFLINE_AFTER_MS / 60000;

const useDotStyles = makeStyles((theme: Theme) => ({
  dot: {
    display: "inline-block",
    flexShrink: 0,
    width: 10,
    height: 10,
    borderRadius: "50%",
    marginRight: theme.spacing(1),
  },
  online: {
    backgroundColor: green[600],
  },
  offline: {
    backgroundColor: orange[600],
  },
}));

// a green dot for online, an orange one for offline; offline is not always a
// fault, as some devices are turned off on purpose
export const StatusDot: React.FC<{ status: "online" | "offline" }> = ({
  status,
}) => {
  const classes = useDotStyles();
  return <span className={`${classes.dot} ${classes[status]}`} />;
};

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    position: "sticky",
    top: NAVBAR_HEIGHT,
    zIndex: theme.zIndex.appBar,
    // the cards scroll under it; the page is white
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(1, 0),
    marginBottom: theme.spacing(1),
  },
  button: {
    textTransform: "none",
    padding: theme.spacing(0.5, 2),
  },
  count: {
    marginLeft: theme.spacing(1),
    fontWeight: theme.typography.fontWeightBold,
  },
}));

export type StatusFilter = "all" | "online" | "offline";

// whether an order of the given status is shown by the filter
export const matchesStatusFilter = (
  filter: StatusFilter,
  status: OnlineStatus
): boolean => "all" === filter || filter === status;

interface Props {
  className?: string;
  value: StatusFilter;
  onChange: (value: StatusFilter) => void;
  online: number;
  offline: number;
}

// Tabs to show all of a grid's orders, or only those online or offline, each
// with how many there are. It stays in view as the page scrolls.
const OnlineStatusFilter: React.FC<Props> = ({
  className,
  value,
  onChange,
  online,
  offline,
}) => {
  const classes = useStyles();
  const tabs: {
    value: StatusFilter;
    label: string;
    count: number;
    title: string;
  }[] = [
    {
      value: "all",
      label: "All",
      count: online + offline,
      title: "Show all orders",
    },
    {
      value: "online",
      label: "Online",
      count: online,
      title: `Show the orders whose device has reported in the last ${OFFLINE_AFTER_MIN} minutes`,
    },
    {
      value: "offline",
      label: "Offline",
      count: offline,
      title: `Show the orders whose device has not reported in ${OFFLINE_AFTER_MIN} minutes`,
    },
  ];
  return (
    <div
      className={className ? `${classes.root} ${className}` : classes.root}
      data-testid="online-status-filter"
    >
      <ToggleButtonGroup
        exclusive
        size="small"
        value={value}
        // a click on the selected tab keeps it selected
        onChange={(_e, v: StatusFilter | null): void => {
          if (v) onChange(v);
        }}
        aria-label="Filter by online status"
      >
        {tabs.map((t) => (
          <ToggleButton
            key={t.value}
            value={t.value}
            className={classes.button}
            title={t.title}
            data-testid={`status-filter-${t.value}`}
          >
            {"all" !== t.value && <StatusDot status={t.value} />}
            {t.label}
            <span
              className={classes.count}
              data-testid={`status-count-${t.value}`}
            >
              {t.count}
            </span>
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </div>
  );
};

export default OnlineStatusFilter;
